/**
 * LikeButton Component
 * Reusable button for liking entities (trips, profiles, itineraries)
 */
import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import { socialService } from '../../services/api/socialService';
import { Like } from '../../types/social.types';
import './LikeButton.css';

interface LikeButtonProps {
  entityType: 'TRIP' | 'PROFILE' | 'ITINERARY';
  entityId: string;
  initialIsLiked?: boolean;
  initialLikeCount?: number;
  size?: 'sm' | 'md' | 'lg';
  showCount?: boolean;
  onLikeChange?: (isLiked: boolean, newCount: number) => void;
}

export const LikeButton: React.FC<LikeButtonProps> = ({
  entityType,
  entityId,
  initialIsLiked = false,
  initialLikeCount = 0,
  size = 'md',
  showCount = true,
  onLikeChange
}) => {
  const [isLiked, setIsLiked] = useState(initialIsLiked);
  const [likeCount, setLikeCount] = useState(initialLikeCount);
  const [loading, setLoading] = useState(false);

  const handleToggleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (loading) return;

    setLoading(true);
    const previousIsLiked = isLiked;
    const previousCount = likeCount;

    // Optimistic update
    setIsLiked(!isLiked);
    setLikeCount((prev) => prev + (isLiked ? -1 : 1));

    try {
      if (isLiked) {
        await socialService.unlikeEntity(entityType, entityId);
      } else {
        await socialService.likeEntity(entityType, entityId);
      }

      const newCount = likeCount + (isLiked ? -1 : 1);
      onLikeChange?.(!isLiked, newCount);
    } catch (error) {
      console.error('Failed to toggle like:', error);
      // Revert on error
      setIsLiked(previousIsLiked);
      setLikeCount(previousCount);
    } finally {
      setLoading(false);
    }
  };

  const sizeClass = `like-button-${size}`;

  return (
    <button
      className={`like-button ${sizeClass} ${isLiked ? 'liked' : ''} ${
        loading ? 'loading' : ''
      }`}
      onClick={handleToggleLike}
      disabled={loading}
      aria-label={isLiked ? 'Unlike' : 'Like'}
    >
      <Heart
        className='like-button-icon'
        fill={isLiked ? 'currentColor' : 'none'}
      />
      {showCount && <span className='like-button-count'>{likeCount}</span>}
    </button>
  );
};
